import { supabase } from './supabase';

/**
 * Tests the connection to Supabase and returns the status
 * @returns A promise resolving to a connection status object
 */
export async function testSupabaseConnection() {
  console.log('Testing Supabase connection...');
  
  try {
    // First try the connection_test function
    const { data: testData, error: testError } = await supabase.rpc('connection_test');
    
    if (!testError) {
      console.log('Supabase connection successful (connection_test):', testData);
      return {
        connected: true,
        url: supabase.supabaseUrl,
        version: testData?.server_version || 'Unknown',
        status: 200,
        method: 'connection_test'
      };
    }
    
    console.log('Trying version function after connection_test failed:', testError);
    
    // If that fails, try the version function
    const { data, error } = await supabase.rpc('version');

    if (error) {
      console.error('Supabase connection error:', error);
      
      // If both fail, try a simple table query as fallback
      try {
        const { error: tableError } = await supabase
          .from('users')
          .select('id')
          .limit(1);
          
        if (!tableError) {
          return {
            connected: true,
            url: supabase.supabaseUrl,
            version: 'Unknown (table access working)',
            status: 200,
            method: 'table_fallback'
          };
        }
        
        return {
          connected: false,
          error: error.message,
          details: {
            code: error.code,
            hint: error.hint,
            message: error.message
          }
        };
      } catch (fallbackError) {
        return {
          connected: false,
          error: error.message,
          fallbackError: fallbackError instanceof Error ? fallbackError.message : 'Unknown error',
          details: error
        };
      }
    }

    // If we got data, connection is working
    console.log('Supabase connection successful (version):', data);
    return {
      connected: true,
      url: supabase.supabaseUrl,
      version: data || 'Unknown',
      status: 200,
      method: 'version'
    };
  } catch (error) {
    // Handle unexpected errors
    console.error('Unexpected error testing Supabase connection:', error);
    
    return {
      connected: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      details: error
    };
  }
}

/**
 * Tests a more complex query to validate database schema access
 */
export async function testDatabaseSchema() {
  console.log('Testing database schema access...');
  
  try {
    // Use proper format for count query
    const { count, error } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.error('Database schema test error:', error);
      
      // Try a simpler fallback query
      try {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('users')
          .select('id')
          .limit(1);
          
        if (!fallbackError) {
          return {
            schemaAccess: true,
            tableCount: fallbackData?.length || 0,
            method: 'fallback_query'
          };
        }
      } catch (e) {
        // Fallback failed, continue with original error
      }
      
      // Check if this is a permissions error
      if (error.code === 'PGRST301' || error.message.includes('permission denied')) {
        return {
          schemaAccess: false,
          error: 'Permission denied. RLS policies might be preventing access.',
          details: error
        };
      }
      
      // Check if this is a table not found error
      if (error.code === '42P01' || (error.message.includes('relation') && error.message.includes('does not exist'))) {
        return {
          schemaAccess: false,
          error: 'Table not found. The migration might not have been applied.',
          details: error
        };
      }
      
      return {
        schemaAccess: false,
        error: error.message,
        details: error
      };
    }

    console.log('Database schema access successful:', count);
    return {
      schemaAccess: true,
      tableCount: count || 0
    };
  } catch (error) {
    console.error('Unexpected error testing database schema:', error);
    
    return {
      schemaAccess: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      details: error
    };
  }
}

/**
 * Tests if the webhook listener function is available
 */
export async function testWebhookListener() {
  console.log('Testing webhook listener function...');
  
  try {
    const testBrowserId = '00000000-0000-0000-0000-000000000000';
    const { data, error } = await supabase.rpc('listen_webhooks', { 
      browser_id: testBrowserId 
    });

    if (error) {
      console.error('Webhook listener test error:', error);
      
      // Try a simpler function as fallback to check if RPC is working at all
      try {
        const { data: testData, error: testError } = await supabase.rpc('connection_test');
        
        if (!testError) {
          return {
            listenerAvailable: false,
            rpcWorking: true,
            error: 'Webhook listener unavailable, but RPC is working.',
            fallbackTest: testData
          };
        }
      } catch (e) {
        // Fallback failed, continue with original error
      }
      
      return {
        listenerAvailable: false,
        error: error.message,
        details: error
      };
    }

    console.log('Webhook listener test successful:', data);
    return {
      listenerAvailable: true,
      response: data
    };
  } catch (error) {
    console.error('Unexpected error testing webhook listener:', error);
    
    return {
      listenerAvailable: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      details: error
    };
  }
}